import React from 'react';
import {
    Card,
    CardHeader,
    Divider,
    Table,
    TableBody,
    makeStyles,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ILeadInfo, TValue } from '../types';
import { TextRow } from '../TextRow';
import { EditableRow } from '../EditableRow';
import { CapitalizeNames } from 'src/utils/capitalize';
import numeral from 'numeral';
import { Label } from 'src/components/Tags';
import useLead from 'src/hooks/useLead';
import { TYPEOF_STRING } from 'src/constants/utils/typeof';
import { AIRowOptions } from '../AIRowOptions';
import { LeadInfoForm } from './components';
import { Formik, FormikValues } from 'formik';
import { ECountryCode } from 'src/modules/forms/fields/AreaCodeTextField/components/AreaCodeSelect';

const useStyles = makeStyles(() => ({
    paper: {
        overflow: 'scroll',
    },
    margin: {
        marginBottom: '1em',
    },
}));

export const LeadInfo = ({ lead, loading, ...props }: ILeadInfo) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { updateLead } = useLead();

    const VALUES: TValue[] = [
        {
            header: t('Leads.TakeCarInTrade'),
            text: lead?.takeCarInTrade ? t('Yes') : t('No'),
        },
        {
            header: t('Leads.Source'),
            text:
                typeof lead?.source === 'object'
                    ? CapitalizeNames(lead?.source?.name)
                    : CapitalizeNames(lead.source),
        },
        {
            header: t('Leads.DownPayment'),
            text: numeral(lead?.downPayment || 0).format('$0,0'),
        },
        {
            header: t('Leads.Timeframe'),
            text: t(lead?.timeFrameString) || t('Messages.NoneO'),
        },
        {
            header: t('Leads.Temperature'),
            text: (
                <Label color={lead.rating}>
                    {lead.rating && t(`Status.${lead.rating}`)}
                </Label>
            ),
        },
        {
            header: t('Leads.Comment'),
            text: lead?.comment || t('Messages.NoneO'),
        },
    ];

    const handleSave = (values: FormikValues) => {
        updateLead(values, lead._id);
    };

    if (!lead?._id) return null;
    return (
        <Card className={classes.paper} {...props}>
            <CardHeader title={t('Leads.Info')} />
            <Divider />
            <Formik
                initialValues={{
                    name: lead?.name,
                    email: lead?.email,
                    phone: lead?.phone,
                    countryCode: lead?.countryCode || ECountryCode.Mexico,
                }}
                enableReinitialize
                onSubmit={handleSave}
            >
                {({
                    values,
                    handleChange,
                    handleBlur,
                    touched,
                    errors,
                    handleSubmit,
                    setFieldValue,
                }) => (
                    <Table className={classes.margin}>
                        <TableBody>
                            <LeadInfoForm
                                {...{
                                    values,
                                    handleChange,
                                    handleBlur,
                                    touched,
                                    errors,
                                    lead,
                                    handleSubmit,
                                    loading,
                                    setFieldValue,
                                }}
                            />
                            {VALUES.map((item, key) =>
                                item.editable &&
                                typeof item.text === TYPEOF_STRING ? (
                                    <EditableRow
                                        key={key}
                                        item={item}
                                        loading={loading}
                                        handleSave={handleSave}
                                    />
                                ) : (
                                    <TextRow
                                        key={key}
                                        item={item}
                                        loading={loading}
                                    />
                                )
                            )}
                            {lead.aiOptions?.isContacted && (
                                <AIRowOptions
                                    options={lead.aiOptions}
                                    loading={loading}
                                />
                            )}
                        </TableBody>
                    </Table>
                )}
            </Formik>
        </Card>
    );
};
